import {useCallback, useEffect, useMemo, useState} from 'react'
import {BASE_URL} from '../../../api'
import config from '../../config'
import {aiDraft, aiError} from '../_shared/aiDraft'
const {axios} = config

// Roadmap #13 (2026-09-29): the in-app FAQ. Seeded from
// enformawellness.com/faqs; edit here and the app updates within a few
// minutes (no app release). Spanish is auto-translated when left blank.
const emptyNew = {section: '', question: '', answer: '', questionEs: '', answerEs: ''}

const Item = ({f, index, total, onMove, reload, setError, sections}) => {
  const [edit, setEdit] = useState(false)
  const [form, setForm] = useState({})
  const [open, setOpen] = useState(false)
  const start = () => {
    setForm({question: f.question, questionEs: f.questionEs, answer: f.answer, answerEs: f.answerEs, section: f.section})
    setEdit(true)
  }
  const save = async () => {
    try {
      const body = {...form}
      if (body.section === f.section) delete body.section
      // Only send Spanish if it was edited, so changed English re-translates.
      if (body.questionEs === f.questionEs) delete body.questionEs
      if (body.answerEs === f.answerEs) delete body.answerEs
      await axios.put(`${BASE_URL}/admin/faqs/${f._id}`, body)
      setEdit(false)
      reload()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save.')
    }
  }
  const toggle = async () => {
    try {
      await axios.put(`${BASE_URL}/admin/faqs/${f._id}`, {hidden: !f.hidden})
      reload()
    } catch (e) {
      setError('Could not update.')
    }
  }
  const remove = async () => {
    if (!window.confirm(`Delete "${f.question}"?`)) return
    await axios.delete(`${BASE_URL}/admin/faqs/${f._id}`)
    reload()
  }
  const ta = (key, rows, ph) => (
    <textarea className='form-control form-control-sm form-control-solid mb-2' rows={rows} placeholder={ph} value={form[key] || ''} onChange={(e) => setForm({...form, [key]: e.target.value})} />
  )
  return (
    <div className={`border-bottom border-gray-200 py-4 ${f.hidden ? 'opacity-50' : ''}`}>
      {edit ? (
        <>
          <div className='row g-3'>
            <div className='col-md-6'>
              <div className='fs-7 text-muted mb-1'>English</div>
              {ta('question', 2, 'Question')}
              {ta('answer', 9, 'Answer')}
              <input className='form-control form-control-sm form-control-solid' list='faq-sections' value={form.section} onChange={(e) => setForm({...form, section: e.target.value})} />
            </div>
            <div className='col-md-6'>
              <div className='fs-7 text-muted mb-1'>Español (leave blank to auto-translate)</div>
              {ta('questionEs', 2, 'Pregunta')}
              {ta('answerEs', 9, 'Respuesta')}
            </div>
          </div>
          <div className='mt-2'>
            <button className='btn btn-sm btn-primary me-2' onClick={save}>Save</button>
            <button className='btn btn-sm btn-light' onClick={() => setEdit(false)}>Cancel</button>
          </div>
        </>
      ) : (
        <div className='d-flex align-items-start gap-3'>
          <div className='text-nowrap'>
            <button className='btn btn-sm btn-icon btn-light me-1' disabled={index === 0} onClick={() => onMove(index, -1)}>↑</button>
            <button className='btn btn-sm btn-icon btn-light' disabled={index === total - 1} onClick={() => onMove(index, 1)}>↓</button>
          </div>
          <div className='flex-grow-1' style={{cursor: 'pointer'}} onClick={() => setOpen(!open)}>
            <div className='fw-bold'>
              {f.question} {f.hidden && <span className='badge badge-light ms-2'>Hidden</span>}
            </div>
            <div className='text-muted fs-7'>{f.questionEs}</div>
            {open && (
              <div className='row mt-3 fs-7'>
                <div className='col-md-6' style={{whiteSpace: 'pre-wrap'}}>{f.answer}</div>
                <div className='col-md-6 text-muted' style={{whiteSpace: 'pre-wrap'}}>{f.answerEs}</div>
              </div>
            )}
          </div>
          <div className='text-nowrap'>
            <button className='btn btn-sm btn-light me-2' onClick={start}>Edit</button>
            <button className='btn btn-sm btn-light me-2' onClick={toggle}>{f.hidden ? 'Show' : 'Hide'}</button>
            <button className='btn btn-sm btn-light-danger' onClick={remove}>Delete</button>
          </div>
        </div>
      )}
    </div>
  )
}

const SectionTitle = ({name, nameEs, index, total, onMove, reload, setError}) => {
  const [edit, setEdit] = useState(false)
  const [to, setTo] = useState(name)
  const [toEs, setToEs] = useState(nameEs)
  const save = async () => {
    try {
      await axios.put(`${BASE_URL}/admin/faqs/section`, {from: name, to, toEs: toEs === nameEs && to !== name ? '' : toEs})
      setEdit(false)
      reload()
    } catch (e) {
      setError('Could not rename the section.')
    }
  }
  return (
    <div className='d-flex align-items-center gap-3 mb-2'>
      {edit ? (
        <>
          <input className='form-control form-control-sm form-control-solid w-250px' value={to} onChange={(e) => setTo(e.target.value)} />
          <input className='form-control form-control-sm form-control-solid w-250px' value={toEs} onChange={(e) => setToEs(e.target.value)} />
          <button className='btn btn-sm btn-primary' onClick={save}>Save</button>
          <button className='btn btn-sm btn-light' onClick={() => setEdit(false)}>Cancel</button>
        </>
      ) : (
        <>
          <h3 className='fw-bolder m-0'>{name}</h3>
          <span className='text-muted'>{nameEs}</span>
          <button className='btn btn-sm btn-link' onClick={() => setEdit(true)}>Rename</button>
          <div className='ms-auto text-nowrap'>
            <button className='btn btn-sm btn-light me-1' disabled={index === 0} onClick={() => onMove(index, -1)}>Move section up</button>
            <button className='btn btn-sm btn-light' disabled={index === total - 1} onClick={() => onMove(index, 1)}>Move section down</button>
          </div>
        </>
      )}
    </div>
  )
}

const Faqs = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [adding, setAdding] = useState(emptyNew)
  const [busy, setBusy] = useState(false)
  const [drafting, setDrafting] = useState(false)

  const load = useCallback(async () => {
    try {
      const {data} = await axios.get(`${BASE_URL}/admin/faqs`)
      setRows(data.data || [])
    } catch (e) {
      setError('Could not load the FAQ.')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => {
    load()
  }, [load])

  // Sections in the order of their first question.
  const sections = useMemo(() => {
    const out = []
    const map = new Map()
    rows.forEach((r) => {
      if (!map.has(r.section)) {
        const s = {name: r.section, nameEs: r.sectionEs, items: []}
        map.set(r.section, s)
        out.push(s)
      }
      map.get(r.section).items.push(r)
    })
    return out
  }, [rows])

  const saveOrder = async (ordered) => {
    setRows(ordered)
    try {
      await axios.put(`${BASE_URL}/admin/faqs/order`, {ids: ordered.map((r) => r._id)})
    } catch (e) {
      setError('Could not save the order.')
      load()
    }
  }
  const moveItem = (si) => (i, dir) => {
    const secs = sections.map((s) => ({...s, items: [...s.items]}))
    const items = secs[si].items
    const [x] = items.splice(i, 1)
    items.splice(i + dir, 0, x)
    saveOrder(secs.flatMap((s) => s.items))
  }
  const moveSection = (i, dir) => {
    const secs = [...sections]
    const [x] = secs.splice(i, 1)
    secs.splice(i + dir, 0, x)
    saveOrder(secs.flatMap((s) => s.items))
  }

  const add = async () => {
    setError('')
    setBusy(true)
    try {
      await axios.post(`${BASE_URL}/admin/faqs`, adding)
      setAdding(emptyNew)
      load()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not add.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <datalist id='faq-sections'>
        {sections.map((s) => (
          <option key={s.name} value={s.name} />
        ))}
      </datalist>
      <div className='card mb-5'>
        <div className='card-body p-9'>
          <h3 className='fw-bolder mb-3'>FAQ</h3>
          <p className='text-muted'>
            The questions in the app's FAQ (menu → FAQs), in this order. Changes show in the app within
            a few minutes - no app update needed. Leave Spanish blank to auto-translate; if you change
            the English and not the Spanish, the Spanish is re-translated. Line breaks are kept, so
            numbered steps can go on their own lines. Links, emails and phone numbers are tappable.
          </p>
          {error && <div className='alert alert-danger py-3'>{error}</div>}
          <div className='fw-bold mb-2'>Add a question</div>
          <div className='row g-2 align-items-start'>
            <div className='col-md-3'>
              <input className='form-control form-control-solid mb-2' list='faq-sections' placeholder='Section (pick or type a new one)' value={adding.section} onChange={(e) => setAdding({...adding, section: e.target.value})} />
              <input className='form-control form-control-solid' placeholder='Question' value={adding.question} onChange={(e) => setAdding({...adding, question: e.target.value})} />
            </div>
            <div className='col-md-8'>
              <textarea className='form-control form-control-solid' rows={4} placeholder='Answer (or a few notes, then ✨ Write answer)' value={adding.answer} onChange={(e) => setAdding({...adding, answer: e.target.value, answerEs: ''})} />
              <button
                type='button'
                className='btn btn-sm btn-light-primary mt-2'
                disabled={drafting || (!adding.question.trim() && !adding.answer.trim())}
                onClick={async () => {
                  setDrafting(true)
                  setError('')
                  try {
                    const d = await aiDraft('faq', adding.answer || adding.question, {question: adding.question})
                    setAdding((a) => ({
                      ...a,
                      question: d.question || a.question,
                      answer: d.answer || a.answer,
                      questionEs: d.questionEs || '',
                      answerEs: d.answerEs || '',
                    }))
                  } catch (e) {
                    setError(aiError(e))
                  } finally {
                    setDrafting(false)
                  }
                }}
              >
                {drafting ? 'Writing…' : '✨ Write answer'}
              </button>
              {adding.answerEs && (
                <div className='text-muted fs-7 mt-2' style={{whiteSpace: 'pre-wrap'}}>
                  <b>ES:</b> {adding.questionEs}
                  {'\n'}
                  {adding.answerEs}
                </div>
              )}
            </div>
            <div className='col-md-1'>
              <button className='btn btn-primary w-100' disabled={busy || !adding.section || !adding.question || !adding.answer} onClick={add}>
                {busy ? '…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {loading && <div className='text-muted'>Loading…</div>}
      {!loading && !rows.length && <div className='text-muted'>No questions yet. The website FAQ loads automatically a minute or two after the backend restarts - refresh this page.</div>}
      {sections.map((s, si) => (
        <div className='card mb-5' key={s.name}>
          <div className='card-body p-6'>
            <SectionTitle name={s.name} nameEs={s.nameEs} index={si} total={sections.length} onMove={moveSection} reload={load} setError={setError} />
            {s.items.map((f, i) => (
              <Item key={f._id} f={f} index={i} total={s.items.length} onMove={moveItem(si)} reload={load} setError={setError} sections={sections} />
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

export default Faqs
